import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Estilos de los elementos que puede traer una respuesta del agente
// (párrafos, listas, código, tablas GFM, etc.) -- mismo criterio de
// dark theme que ContextualHelpDrawer.jsx (colores de acento consistentes:
// #1a73e8 para links/bordes, igual que los botones/tabs del chat), pero
// con la paleta gris propia de este componente (#a0a0a0/#c9c9c9) en vez del
// blanco puro que usa el drawer de ayuda.
const MARKDOWN_SX = {
    '& > *:first-of-type': { mt: 0 },
    '& > *:last-child': { mb: 0 },
    '& p': { color: 'var(--wb-x-text-a0a0a0, #a0a0a0)', lineHeight: 1.6, m: 0, mb: 1.25 },
    '& h1, & h2, & h3, & h4': { color: 'var(--wb-x-text-e8e8e8, #e8e8e8)', fontWeight: 600, mt: 2, mb: 1, lineHeight: 1.3 },
    '& h1': { fontSize: '1.35rem' },
    '& h2': { fontSize: '1.2rem' },
    '& h3': { fontSize: '1.08rem' },
    '& h4': { fontSize: '1rem' },
    '& strong': { color: 'var(--wb-x-text-d8d8d8, #d8d8d8)', fontWeight: 600 },
    '& em': { color: 'var(--wb-x-text-c9c9c9, #c9c9c9)' },
    '& ul, & ol': { color: 'var(--wb-x-text-a0a0a0, #a0a0a0)', pl: 3, mb: 1.25 },
    '& li': { mb: 0.5, lineHeight: 1.55 },
    '& li > ul, & li > ol': { mb: 0, mt: 0.5 },
    '& a': { color: 'var(--wb-x-text-4d9bff, #4d9bff)', textDecoration: 'underline', textUnderlineOffset: '2px' },
    '& code': {
        backgroundColor: 'var(--wb-overlay-08)',
        color: 'var(--wb-x-text-e8c07d, #e8c07d)',
        borderRadius: 4,
        px: 0.6,
        py: 0.1,
        fontSize: '0.85em',
        fontFamily: '"Roboto Mono", "Courier New", monospace'
    },
    '& pre': {
        backgroundColor: 'var(--wb-x-bg-161b22, #161b22)',
        border: '1px solid var(--wb-overlay-08)',
        borderRadius: 8,
        p: 1.5,
        mb: 1.25,
        overflowX: 'auto'
    },
    '& pre code': {
        backgroundColor: 'transparent',
        color: 'var(--wb-x-text-d6d6d6, #d6d6d6)',
        p: 0,
        fontSize: '0.82em',
        lineHeight: 1.5
    },
    '& blockquote': {
        borderLeft: '3px solid var(--wb-blue-google)',
        pl: 1.5,
        ml: 0,
        mb: 1.25,
        color: 'var(--wb-x-text-8c8c8c, #8c8c8c)',
        fontStyle: 'italic'
    },
    '& table': {
        borderCollapse: 'collapse',
        width: '100%',
        mb: 1.25,
        fontSize: '0.88em'
    },
    '& th, & td': {
        border: '1px solid var(--wb-x-border-ffffff1f, rgba(255,255,255,0.12))',
        padding: '6px 10px',
        textAlign: 'left',
        color: 'var(--wb-x-text-a0a0a0, #a0a0a0)'
    },
    '& th': { color: 'var(--wb-x-text-d8d8d8, #d8d8d8)', backgroundColor: 'var(--wb-x-bg-ffffff0d, rgba(255,255,255,0.05))', fontWeight: 600 },
    '& hr': { border: 'none', borderTop: '1px solid var(--wb-x-border-ffffff1f, rgba(255,255,255,0.12))', my: 1.5 }
};

const ChatStreamSimulator = React.memo(({
  message = '',
  typingSpeed = 30,
  onComplete = () => {},
  className = '',
  showCursor = true,
  instant = false,
  markdown = false,
}) => {
  const [displayedText, setDisplayedText] = useState(instant ? message : '');
  const [currentIndex, setCurrentIndex] = useState(instant ? message.length : 0);
  const [isComplete, setIsComplete] = useState(instant);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    // instant=true (historial ya guardado): mostrar el texto completo de una
    // sola vez, sin animar -- no tiene sentido re-escribir carácter por
    // carácter algo que el usuario ya leyó antes.
    if (instant) {
      setDisplayedText(message);
      setCurrentIndex(message.length);
      setIsComplete(true);
      return;
    }
    // Reset when message changes
    setDisplayedText('');
    setCurrentIndex(0);
    setIsComplete(false);
  }, [message, instant]);

  useEffect(() => {
    if (instant || isPaused || isComplete) return;

    if (currentIndex < message.length) {
      const timer = setTimeout(() => {
        // Add next character
        setDisplayedText(prev => prev + message[currentIndex]);
        setCurrentIndex(prev => prev + 1);

        // Simulate variable typing speed for realism
        const nextChar = message[currentIndex + 1];
        if (nextChar === ':' || nextChar === '.' || nextChar === '?' || nextChar === '!') {
          setIsPaused(true);
          setTimeout(() => setIsPaused(false), typingSpeed * 10);
        }
      }, typingSpeed);

      return () => clearTimeout(timer);
    } else if (!isComplete) {
      setIsComplete(true);
      onComplete();
    }
  }, [currentIndex, message, typingSpeed, isComplete, onComplete, isPaused, instant]);

  const handlePauseResume = () => {
    setIsPaused(prev => !prev);
  };

  const cursor = showCursor && !isComplete && <span className="cursor-blink"> ▌</span>;

  if (markdown) {
    // `displayedText` es lo que ya se "tipeó" hasta ahora -- se re-parsea
    // como Markdown en cada carácter nuevo (igual que hacen ChatGPT/Claude
    // al mostrar la respuesta en vivo). Un `**negrita` sin cerrar se ve
    // literal un instante hasta que llega el cierre -- es una degradación
    // aceptable y esperable de este patrón, no un bug.
    // `<div>` (no `<span>`): ReactMarkdown puede renderizar elementos de
    // bloque (p, ul, h2, pre...), que no son válidos dentro de un inline.
    return (
        <Box component="div" className={className} sx={MARKDOWN_SX}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{displayedText}</ReactMarkdown>
            {cursor}
        </Box>
    );
  }

  return (
    <span className={className} style={{ whiteSpace: 'pre-line' }}>
        {displayedText}{cursor}
    </span>
  );
});

export default ChatStreamSimulator;
